import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Quran Store - manages all app state with localStorage persistence
export const useQuranStore = create(
  persist(
    (set, get) => ({
      // Theme
      theme: 'system',
      setTheme: (theme) => set({ theme }),
      
      // Selected reciter (1-5)
      reciterId: '1',
      setReciterId: (reciterId) => set({ reciterId }),
      
      // Translation language
      translationLang: 'english',
      setTranslationLang: (translationLang) => set({ translationLang }),
      
      // Show Arabic diacritics (arabic1 vs arabic2)
      showDiacritics: true,
      setShowDiacritics: (showDiacritics) => set({ showDiacritics }),
      
      // Font size multiplier
      fontSize: 1,
      setFontSize: (fontSize) => set({ fontSize }),
      
      // Reading progress - last position
      lastReading: null, // { surahNo, ayahNo, surahName, timestamp }
      setLastReading: (surahNo, ayahNo, surahName) => set({ 
        lastReading: { surahNo, ayahNo, surahName, timestamp: Date.now() } 
      }),
      
      // Bookmarked verses
      bookmarks: [], // [{ surahNo, ayahNo, surahName, arabic, english, timestamp }]
      addBookmark: (bookmark) => set((state) => {
        const exists = state.bookmarks.some(
          b => b.surahNo === bookmark.surahNo && b.ayahNo === bookmark.ayahNo
        );
        if (exists) return state;
        return { bookmarks: [...state.bookmarks, { ...bookmark, timestamp: Date.now() }] };
      }),
      removeBookmark: (surahNo, ayahNo) => set((state) => ({
        bookmarks: state.bookmarks.filter(
          b => !(b.surahNo === surahNo && b.ayahNo === ayahNo)
        )
      })),
      isBookmarked: (surahNo, ayahNo) => {
        return get().bookmarks.some(
          b => b.surahNo === surahNo && b.ayahNo === ayahNo
        );
      },
      
      // Favorite surahs
      favorites: [], // [{ surahNo, surahName, surahNameArabic, timestamp }]
      addFavorite: (surah) => set((state) => {
        const exists = state.favorites.some(f => f.surahNo === surah.surahNo);
        if (exists) return state;
        return { favorites: [...state.favorites, { ...surah, timestamp: Date.now() }] };
      }),
      removeFavorite: (surahNo) => set((state) => ({
        favorites: state.favorites.filter(f => f.surahNo !== surahNo)
      })),
      isFavorite: (surahNo) => {
        return get().favorites.some(f => f.surahNo === surahNo);
      },
      
      // Memorization progress
      // { surahNo_ayahNo: { repetitions: number, lastPracticed: timestamp, status: 'learning'|'review'|'memorized' } }
      memorizationProgress: {},
      
      updateMemorizationProgress: (surahNo, ayahNo, repetitions) => set((state) => {
        const key = `${surahNo}_${ayahNo}`;
        const current = state.memorizationProgress[key] || { repetitions: 0 };
        const newReps = current.repetitions + repetitions;
        let status = 'learning';
        if (newReps >= 10) status = 'memorized';
        else if (newReps >= 5) status = 'review';
        
        return {
          memorizationProgress: {
            ...state.memorizationProgress,
            [key]: {
              repetitions: newReps,
              lastPracticed: Date.now(),
              status,
            }
          }
        };
      }),
      
      getMemorizationStatus: (surahNo, ayahNo) => {
        const key = `${surahNo}_${ayahNo}`;
        return get().memorizationProgress[key] || { repetitions: 0, status: 'new' };
      },
      
      resetMemorization: (surahNo, ayahNo) => set((state) => {
        const key = `${surahNo}_${ayahNo}`;
        const newProgress = { ...state.memorizationProgress };
        delete newProgress[key];
        return { memorizationProgress: newProgress };
      }),
      
      // Audio player state (not persisted)
      currentAudio: null, // { surahNo, ayahNo, url, reciter }
      isPlaying: false,
      autoAdvance: true, // Auto-play next verse
      setCurrentAudio: (audio) => set({ currentAudio: audio }),
      setIsPlaying: (isPlaying) => set({ isPlaying }),
      setAutoAdvance: (autoAdvance) => set({ autoAdvance }),
    }),
    {
      name: 'ahmad-quran-storage',
      partialize: (state) => ({
        theme: state.theme,
        reciterId: state.reciterId,
        translationLang: state.translationLang,
        showDiacritics: state.showDiacritics,
        fontSize: state.fontSize,
        lastReading: state.lastReading,
        bookmarks: state.bookmarks,
        favorites: state.favorites,
        memorizationProgress: state.memorizationProgress,
        autoAdvance: state.autoAdvance,
      }),
    }
  )
);

export default useQuranStore;
